import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { TriangleAlert, Wallet } from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import { formatLbp, formatUsd } from '@/lib/money'
import { Badge, EmptyState, Toggle } from '@/components/ui/primitives'
import { useSession } from '@/stores/session'
import { Permission } from '@/lib/permissions'
import { t } from '@/i18n'

interface DrawerSettings {
  perCashierDrawer: boolean
}

/** A till as the drawer picker sees it — active ones only, no management fields. */
interface DrawerStatus {
  registerId: number
  registerName: string
  branchId: number
  branchName: string
  shiftOpen: boolean
  openedByDisplayName: string | null
  expectedUsd: number | null
  expectedLbp: number | null
}

/** The fuller shape, including tills out of service and who holds each. */
interface RegisterAdminRow {
  id: number
  name: string
  branchId: number
  branchName: string
  isActive: boolean
  shiftOpen: boolean
  openedByDisplayName: string | null
  assignedToDisplayName: string | null
  expectedUsd: number | null
  expectedLbp: number | null
}

/** One list for both audiences, so a till reads the same however you reached it. */
interface TillRow {
  id: number
  name: string
  branchName: string
  isActive: boolean
  shiftOpen: boolean
  openedByDisplayName: string | null
  assignedToDisplayName: string | null
  expectedUsd: number | null
  expectedLbp: number | null
}

export function DrawersSection() {
  const queryClient = useQueryClient()
  const canManage = useSession((state) => state.can)(Permission.ManageSettings)
  const canView = useSession((state) => state.can)(Permission.ManageSafe)

  const [perCashier, setPerCashier] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)


  const settings = useQuery({
    queryKey: ['settings', 'drawers'],
    queryFn: () => apiClient.get<DrawerSettings>('/api/settings/drawers'),
  })

  useEffect(() => {
    if (settings.data) setPerCashier(settings.data.perCashierDrawer)
  }, [settings.data])

  const save = useMutation({
    mutationFn: (value: boolean) =>
      apiClient.put('/api/settings/drawers', { perCashierDrawer: value }),
    onSuccess: async () => {
      setError(null)
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
      await queryClient.invalidateQueries({ queryKey: ['settings', 'drawers'] })
      await queryClient.invalidateQueries({ queryKey: ['registers'] })
    },
    onError: (caught) => {
      setPerCashier(settings.data?.perCashierDrawer ?? false)
      setError(caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not save.')
    },
  })

  // Whoever can manage settings gets the fuller list, because retired tills only appear there
  // and this is the one screen they can be brought back from.
  const managed = useQuery({
    queryKey: ['registers', 'manage'],
    queryFn: () => apiClient.get<RegisterAdminRow[]>('/api/registers/manage'),
    enabled: canManage,
  })

  const drawers = useQuery({
    queryKey: ['registers', 'all'],
    queryFn: () => apiClient.get<DrawerStatus[]>('/api/registers/all'),
    enabled: !canManage && canView,
  })


  const tills: TillRow[] = managed.data
    ? managed.data.map((row) => ({
        id: row.id,
        name: row.name,
        branchName: row.branchName,
        isActive: row.isActive,
        shiftOpen: row.shiftOpen,
        openedByDisplayName: row.openedByDisplayName,
        assignedToDisplayName: row.assignedToDisplayName,
        expectedUsd: row.expectedUsd,
        expectedLbp: row.expectedLbp,
      }))
    : (drawers.data ?? []).map((row) => ({
        id: row.registerId,
        name: row.registerName,
        branchName: row.branchName,
        isActive: true,
        shiftOpen: row.shiftOpen,
        openedByDisplayName: row.openedByDisplayName,
        assignedToDisplayName: null,
        expectedUsd: row.expectedUsd,
        expectedLbp: row.expectedLbp,
      }))

  // Surfaced rather than swallowed. An error here is the difference between "this shop has no
  // tills" and "the server would not say", and only one of those is the owner's problem.
  const failed = managed.error ?? drawers.error
  const loadError = failed
    ? failed instanceof ApiError
      ? (failed.detail ?? failed.message)
      : 'the server did not answer'
    : null


  return (
    <div>
      <p className="mb-4 text-sm text-[var(--text-secondary)]">
        {t('Choose whether cashiers on a branch share one drawer, or each has their own.')}
      </p>

      {canManage && (
        <div className="surface-raised mb-5 rounded-[var(--radius-card)] border p-4">
          <Toggle
            checked={perCashier}
            onChange={(value) => {
              setPerCashier(value)
              save.mutate(value)
            }}
            label={t('Each cashier has their own drawer')}
            hint={
              perCashier
                ? 'A till is created for every cashier and manager automatically. Each opens and closes their own.'
                : 'Everyone on a branch opens and closes the same shared till.'
            }
          />
          {error && (
            <p role="alert" className="mt-3 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
              {error}
            </p>
          )}
          {saved && <p className="mt-3 text-sm text-success-500">{t('Saved')}</p>}
        </div>
      )}

      {(canManage || canView) && (
        <div>
          <div className="mb-3">
            <h2 className="text-sm font-medium">{t('Tills')}</h2>
            <p className="mt-0.5 text-xs text-[var(--text-secondary)]">
              {/* Says plainly that this list is not edited here, so nobody hunts for the button. */}
              {perCashier
                ? 'One till each, named after the person who works it. Renaming or disabling someone changes their till with them.'
                : 'One shared till per branch. Everyone on shift opens and closes the same drawer.'}
            </p>
          </div>

          {/*
            A failed request must not look like an empty shop. Rendering "No tills yet" when the
            server refused the question sends an owner hunting for tills that were never missing —
            the commonest cause being a tenant database that has not had its migrations applied,
            which makes this endpoint fail outright.
          */}
          {loadError ? (
            <p role="alert" className="flex items-start gap-2 rounded-lg bg-danger-500/10 px-3 py-3 text-sm text-danger-500">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Could not load the tills — {loadError}. Nothing has been changed. If this store was
                recently updated, apply its pending migrations from the platform panel.
              </span>
            </p>
          ) : tills.length === 0 ? (
            <EmptyState
              icon={Wallet}
              title={t('No tills yet')}
              description={t('Add one before anybody can sell.')}
            />
          ) : (
            <ul className="flex flex-col gap-2">
              {tills.map((till) => (
                <li
                  key={till.id}
                  className="surface-raised flex items-center justify-between gap-3 rounded-[var(--radius-card)] border p-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate font-medium">{till.name}</span>
                      {!till.isActive ? (
                        <Badge tone="neutral">{t('Out of service')}</Badge>
                      ) : (
                        <Badge tone={till.shiftOpen ? 'accent' : 'neutral'}>
                          {till.shiftOpen ? 'Open' : 'Closed'}
                        </Badge>
                      )}
                    </div>
                    <p className="truncate text-xs text-[var(--text-secondary)]">
                      {till.branchName}
                      {till.assignedToDisplayName ? ` · ${till.assignedToDisplayName}'s` : ''}
                      {till.shiftOpen && till.openedByDisplayName
                        ? ` · opened by ${till.openedByDisplayName}`
                        : ''}
                    </p>
                  </div>

                  {till.expectedUsd !== null && (
                    <div className="tabular shrink-0 text-right">
                      <p className="font-semibold">{formatUsd(till.expectedUsd)}</p>
                      {till.expectedLbp !== null && till.expectedLbp !== 0 && (
                        <p className="text-xs text-[var(--text-secondary)]">
                          {formatLbp(till.expectedLbp)}
                        </p>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

    </div>
  )
}
